import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
// eslint-disable-next-line no-restricted-imports
import * as AkLogo from '@atlaskit/logo';
import { getProductConfig } from './productConfig';
import type { UiProductType } from '../types';

const messages = defineMessages({
  productIconAlt: {
    id: 'appUpdates.productIcon.alt',
    defaultMessage: '{productName} icon',
    description: 'Alt text for app icon',
  },
  unknownProductIcon: {
    id: 'appUpdates.productIcon.unknown',
    defaultMessage: 'App icon',
    description: 'Alt text for unknown app icon',
  },
});

export interface ProductIconProps {
  /** Product type to render icon for */
  productType?: UiProductType;
  /** Size of the icon */
  size?: 'xsmall' | 'small' | 'medium' | 'large';
  /** Whether to use icon (true) or logo (false) variant */
  useIcon?: boolean;
  /** Test ID for testing */
  testId?: string;
}

/**
 * ProductIcon component that renders an appropriate AtlasKit logo component
 * for a given product type or key, with fallback to text display for unknown products.
 */
export const ProductIcon: React.FC<ProductIconProps> = ({
  productType,
  size = 'small',
  useIcon = true,
  testId,
}) => {
  const { formatMessage } = useIntl();
  const productConfig = productType ? getProductConfig(productType) : null;

  // If no product config found, fallback to AtlassianIcon
  if (!productConfig) {
    return (
      <AkLogo.AtlassianIcon
        size={size}
        appearance="brand"
        testId={testId || 'product-icon-fallback'}
        label={formatMessage(messages.unknownProductIcon)}
      />
    );
  }

  // Determine which component to use (icon or logo)
  const componentName = useIcon
    ? productConfig.icon
    : productConfig.logo || productConfig.icon;

  // Get the component from AtlasKit logo library with type assertion
  const LogoComponent = (AkLogo as any)[componentName];

  if (!LogoComponent || typeof LogoComponent !== 'function') {
    // Fallback to AtlassianIcon if component not found
    return (
      <AkLogo.AtlassianIcon
        size={size}
        appearance="brand"
        testId={testId || 'product-icon-fallback'}
        label={formatMessage(messages.unknownProductIcon)}
      />
    );
  }

  return (
    <LogoComponent
      size={size}
      appearance="brand"
      testId={testId || `product-icon-${componentName}`}
      label={formatMessage(messages.productIconAlt, {
        productName: productConfig.displayName,
      })}
    />
  );
};
